import BlogArticle from 'components/blog/BlogArticle';
import MainLayout from 'components/layout/MainLayout';
import RoundedButton from 'components/RoundedButton';
import Separator from 'components/Separator';
import { NextSeo } from 'next-seo';
import { getCta } from 'utils/common';
import { ASSET_PREFIX } from 'utils/constants';

const Article = () => {
  const IMAGE_DIRECTORY = `${ASSET_PREFIX}/images/blog/articles/best-halloween-events-around-the-world`;

  return (
    <MainLayout>
      <NextSeo
        title="Best Halloween Events | Win Epic Prizes | DAYMADE"
        description="We list the best Halloween events around the world, from Macnas Halloween Parade in Galway to the Samhuinn Fire Festival in Edinburgh"
        openGraph={{
          title: 'Best Halloween Events | Win Epic Prizes | DAYMADE',
          description:
            'We list the best Halloween events around the world, from Macnas Halloween Parade in Galway to the Samhuinn Fire Festival in Edinburgh',
          type: 'article',
          url: 'https://www.daymade.co.uk/blog/best-halloween-events-around-the-world',
          images: [
            {
              url: `${IMAGE_DIRECTORY}/hero.jpg`,
              alt: 'Group of people wearing various hooded costumes marching through and archway of fire carrying medieval torches',
            },
          ],
        }}
        // additionalMetaTags={[{
        //   name: 'keywords',
        //   content: 'Halloween,spooktacular,macnas,parade,galway,event,Kawasaki,salem,salem witches ball,alton towers,scarefest,wicker man,Dracula,Transylvania,bran castle,Glasgow,fairground,samhuinn,fire festival,procession,nyc village,eastern state,penitentiary,haunted house,win a prize,blog,best Halloween events',
        // }]}
      />

      <BlogArticle
        imageDirectory={IMAGE_DIRECTORY}
        heroAltText="Group of people wearing various hooded costumes marching through and archway of fire carrying medieval torches"
        heroImageYPosition="33%"
      >
        <>
          <h1 style={{ fontSize: '1.6em', lineHeight: '1.4em' }}>
            The Best <strong>Halloween Events</strong> Around the World
          </h1>

          <p className="subheading">Our top 11 spooktacular goings-on</p>

          <div className="separator" style={{ width: '80%', marginTop: 0 }} />

          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'center',
              marginBottom: '2em',
            }}
          >
            by Abbi James
          </p>

          <p>
            <span className="bigFirstLetter">I</span>t’s officially the run-up
            to Halloween, and we couldn’t help but explore some of the very best
            spooktacular Halloween events around the world!
          </p>

          <h3>Jack-O’-Lantern Spectacular in Louisville, USA</h3>

          <p>
            This picturesque pumpkin bonanza is a visual treat to behold.
            Imagine taking a dusk-time stroll through a forested hillside whilst
            surrounded by 5,000 carved and decorated pumpkins… all eyes on you!
            Even better, 100 pumpkins are carved into veritable works of art,
            each one as unique as the last. This family-friendly fun started
            back in Oxford, Massachusetts in 1988, before being so popular it
            spread across to Louisville. Each year brings a new theme so no two
            shows are the same!
          </p>

          <h3>Macnas Halloween Parade, Galway, Ireland</h3>

          <p>
            What do you get when you mix pyrotechnics, bespoke costumes,
            sculptural creations and live music? A magical Macnas parade, that’s
            what! The streets of Galway fill with 70,000 people surrounded by
            mesmerising sights, sounds and smells on a night like no other.
            Macnas conjure up a new world for you to immerse yourself in, with
            hundreds of performers from the likes of the Macnas Young Ensemble,
            Youth Ballet West and the Macnas Youth Drummers. And did we tell you
            about the giant creations – the stars of the show? Gotta see em to
            believe em…
          </p>

          <div className="doubleImageContainer">
            <img
              src={`${IMAGE_DIRECTORY}/macnas-1.jpg`}
              alt="20-foot unicorn float being paraded down road at night"
              className="doubleBlogImage"
            />

            <img
              src={`${IMAGE_DIRECTORY}/macnas-2.jpg`}
              alt="20-foot witch float carrying lantern being paraded down road at night"
              className="doubleBlogImage"
            />
          </div>

          <h3>Universal Studios Japan Horror Night, Osaka, Japan</h3>

          <p>
            Celebrating Halloween has become increasingly popular in Japan, with
            events taking place in all major cities. Whilst for many the biggest
            element of Halloween is dressing up, Universal Studios Japan has
            brought the spook back to spooktober. This Halloween Horror Night
            features zombies roaming the streets, horror theatre and the live
            performance of NO LIMIT! Halloween Festa. For those familiar with
            the Japanese horror anime GeGeGe no Kitaro, you’ll also encounter
            terrifying Japanese Yokai…don’t say we didn’t warn you!
          </p>

          <h3>Kawasaki Halloween Parade, Tokyo, Japan</h3>

          <p>
            Japan’s biggest Halloween parade is held in Kawasaki, near Tokyo,
            where over 100,000 people gather and line the streets to get
            immersed in this vivacious spectacle. Parade participants don
            intricate, colourful costumes hoping to win the grand prize, whilst
            spectators dress up to enjoy horror movies, face painting and a
            lively afterparty sure to blow your socks off. This annual event
            might have been cancelled for 2021, but we’re hoping it’ll make a
            comeback next year!
          </p>

          <h3>Alton Towers Scarefest, Staffordshire, UK</h3>

          <p>
            Scarefest is renowned as one of the UK’s best Halloween events and
            has firmly secured a place on our to-do list. The award-winning
            Scarefest is filled with hair-raising and spooky attractions, sure
            to give you a frightful night! The Wicker Man and Alton Towers
            Dungeon are just the beginning, with four terrifying mazes ready to
            scare you silly. From Darkest Depths to Freak Show: Toxic Junkyard,
            you’ll be hunted and haunted as you escape the thrilling mazes.
            Trick o’ Treat Town is more family-friendly, giving gentle spooks
            suitable for the whole family.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/alton-towers.jpg`}
            alt="Actors in scary costumes posing with various props including a chainsaw"
            className="singleBlogImage"
          />

          <h3>
            Haunted Happenings Grand Parade & The Salem Witches’ Halloween Ball,
            Massachusetts, USA
          </h3>

          <p>
            It’s no surprise that Salem is a must-visit destination for
            Halloween fans. Salem is infamous for the widely-known Salem Witch
            Trials, where 25 ‘witches’ were executed in the 17th Century. An
            entire month of scary events lead up to the celebrations of October
            31st, many of which tend to be darker than a typical costume contest
            or pumpkin carving competition. The Haunted Happenings Grand Parade
            features performances, floats and displays, kicking off the
            month-long celebrations with a night of colour, music and intricate
            costumes. For an immersive twist, it can’t get more intense than The
            Salem Witches’ Halloween Ball. The night is filled with rituals,
            live music from the Dragon Ritual Drummers, and costumes galore.
            You’ll join the Witches of Salem to honour the dead and celebrate
            all things magical. Many make the journey from around the globe to
            attend, and whilst this year might be slightly different than usual,
            it’s still sure to be an impactful evening.
          </p>

          <h3>Dracula’s Castle Halloween Party, Transylvania, Romania</h3>

          <p>
            Transylvania is synonymous with howling wolves and bloodthirsty
            vampires, most notably Dracula himself. Whilst you can’t exactly
            have a meet-and-greet with Dracula, you can visit Bran Castle –
            widely renowned as Dracula’s Castle. Decorated for the ultimate
            Halloween party, Bran Castle is filled with some of the world’s top
            vampire fans donning realistic costumes ready to party all night
            long. The legendary Transylvanian fortress dates back to 1377,
            making it the perfect backdrop for a traditional Transylvanian
            festive dinner. Whilst visiting Bran Castle, take some time to
            explore the Carpathian Mountains.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/bran-castle.jpg`}
            alt="Old castle at top of hill peaking out from the trees that surround it"
            className="singleBlogImage"
          />

          <h3>Spooktacular, Glasgow, UK</h3>

          <p>
            October gets positively spooky in Glasgow, as Spooktacular comes to
            town! This fiendish fairground is filled to the brim with thrills
            and chills a-plenty. With nineteen different rides, there’s
            something for everyone from the terrifyingly towering roller coaster
            down to the family-friendly funhouse. And of course no fairground is
            complete without the smell of candyfloss and churros wafting through
            the air…
          </p>

          <h3>Samhuinn Fire Festival, Edinburgh, UK</h3>

          <p>
            The Samhuinn Fire Festival is a visual spectacle to behold, marking
            the annual transition from Summer to Winter since 1995. There’s
            immersive performances, intense drumming and of course a LOT of
            fire! It’s a self-declared investigative outdoor theatre, where
            spectators can immerse themselves in the night’s colourful and wild
            atmosphere. There’s also the option to affiliate yourself with
            Winter, Summer, the Cailleach, or all three during the festival
            processions, each giving you a different view of the night’s events.
            Due to the nature of the festival, each year’s events offer a
            different experience meaning it’s worth going back time and time
            again.
          </p>

          <div className="doubleImageContainer">
            <img
              src={`${IMAGE_DIRECTORY}/hero.jpg`}
              alt="Group of people wearing various hooded costumes marching through and archway of fire carrying medieval torches"
              className="doubleBlogImage"
            />

            <img
              src={`${IMAGE_DIRECTORY}/samhuinn-2.jpg`}
              alt="Costumed man breathing fire in front of crown of spectators"
              className="doubleBlogImage"
            />
          </div>

          <h3>NYC Village Halloween Parade, New York, USA</h3>

          <p>
            We’ve all seen photos of the stunning NYC Village Halloween Parade,
            with elaborate costumes galore! This annual event is like one big
            street party, with there only being one rule: costumes. are.
            mandatory. Where else could you see a zombie-Jedi battling a
            robot-monster?! The annual parade has a theme each year, with 2021’s
            being “Let’s Play”! to honour the children of NYC. 50,000 celebrates
            participate in the parade, making it an unmissable event come
            October 31st.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/nyc-halloween-parade.jpg`}
            alt="Group of roughly 30 people all dressed up in halloween masks and costumes"
            className="singleBlogImage"
          />

          <h3>
            Halloween Nights at Eastern State Penitentiary, Philadelphia, USA
          </h3>

          <p>
            The Eastern State Penitentiary is one of the world’s most infamous
            prisons, but today stands in ruin. Once ‘home’ to the likes of
            “Scarface” Al Capone and “Slick Willie” Sutton, the towering
            landmark is transformed come October into a Halloween horror. The
            Cellblocks hold 15 attractions to unnerve even the bravest visitors.
            You’ll work your way through four haunted houses, an immersive
            walk-through experience, and three live interactive performances
            before escaping to the themed bars and lounges that do their best to
            calm your frights. This isn’t one for the faint of heart, you’ve
            been warned…
          </p>

          <div className="doubleImageContainer">
            <img
              src={`${IMAGE_DIRECTORY}/esp-1.jpg`}
              alt="Creepy man wearing realistic pig mask and butchers apron, and wielding a large meat-knife"
              className="doubleBlogImage"
            />

            <img
              src={`${IMAGE_DIRECTORY}/esp-2.jpg`}
              alt="Deserted prison showing its age with peeling wallpaper and dim lighting"
              className="doubleBlogImage"
            />
          </div>

          <Separator style={{ margin: '2em' }} />

          <p>
            Did we miss out your fave Halloween event?{' '}
            <a
              href="mailto:hello@daymade.co.uk"
              style={{
                margin: 0,
                fontWeight: 'bold',
                color: 'inherit',
                textDecoration: 'underline',
              }}
            >
              Let us know
            </a>{' '}
            your top ghoulish gathering for next year…
          </p>

          <p>
            Did you know that you can win a £25k lastminute.com gift card or a
            £500 LuckyTrip gift card in our Weekly Draws? You could use it to
            travel the world and see these global events!
          </p>

          <RoundedButton
            href={getCta()?.url}
            style={{ minWidth: '180px', marginTop: '1.5em' }}
          >
            Enter Now
          </RoundedButton>
        </>
      </BlogArticle>
    </MainLayout>
  );
};

export default Article;
